<template>
  <div v-if="showTools && !showColPalette" class="studio-controls left !pr-3">
    <div
      v-if="showLayerSelector"
      class="layer-selector-wrapper ml-3 mb-2"
      :class="{'layer-selector-wrapper--locked': layerSelectorDisabled}"
      @click.capture="onLayerSelectorInteraction"
    >
      <TmlNewSelect
        v-model="activeLayer"
        small
        class="text-base"
        :options="layerOptions"
        :disabled="layerSelectorDisabled"
      />
    </div>
  </div>
  <div>
    <div
      class="studio-controls left"
      :class="{'with-sub-menu': dropDownOpen, secondary: !showColPalette}"
      @click.stop
    >
      <template v-if="showTools">
        <template v-if="!showColPalette">
          <a
            v-tml-tooltip.hover.right.nomobile.dieonclick="{
              message: 'Zoom in/out',
            }"
            href="#"
            title="Zoom"
            class="grey"
            @click.prevent="$emit('zoom')"
          >
            <font-awesome-icon :icon="getZoomIcon()" />
          </a>
          <a
            v-if="config.enableShareTool && !showColPalette"
            v-tml-tooltip.hover.right.nomobile.dieonclick="{
              message: 'Share',
            }"
            href="#"
            title="Share design"
            class="grey"
            @click.prevent="shareDesign"
          >
            <font-awesome-icon :icon="faShareAlt" />
          </a>

          <a
            v-if="activeEl || (background && background.fill)"
            v-tml-tooltip.hover.right.nomobile.dieonclick="{
              message: 'Recolour',
            }"
            href="#"
            class="grey col-picker"
            title="Recolour"
            @click.prevent="toggleColPalette()"
          >
            <template v-if="activeEl">
              <div v-if="activeEl.color" class="color-preview-container">
                <div
                  class="color-preview"
                  :style="{backgroundColor: activeEl.color}"
                />
              </div>
              <font-awesome-icon v-else :icon="faPalette" />
            </template>
            <div
              v-else-if="background && background.fill"
              class="color-preview-container"
            >
              <div
                class="color-preview"
                :style="{backgroundColor: background.fill}"
              />
            </div>
          </a>

          <template v-if="activeEl">
            <a
              v-if="activeEl.img"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Filter',
              }"
              href="#"
              class="grey"
              title="Filter"
              @click.prevent="$emit('filter')"
            >
              <font-awesome-icon :icon="faSlidersH" />
            </a>
            <a
              v-if="activeEl.className === 'Image'"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Crop',
              }"
              href="#"
              class="grey"
              title="Crop"
              @click.prevent="activeEl.toggleCropMode()"
            >
              <font-awesome-icon :icon="faCrop" />
            </a>
            <a
              v-if="
                activeEl.className === 'Image' || activeEl.className === 'Text'
              "
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Perspective Shift',
              }"
              href="#"
              class="grey"
              title="Perspective Shift"
              @click.prevent="activeEl.togglePerspectiveMode()"
            >
              <font-awesome-icon class="perspective-shift" :icon="faTh" />
            </a>
            <a
              v-if="activeEl.className === 'Image'"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Fill',
              }"
              href="#"
              class="grey"
              title="Fill"
              @click.prevent="fill"
            >
              <font-awesome-icon :icon="faExpandArrowsAlt" />
            </a>
            <a
              v-if="activeEl.className === 'Image'"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Flip',
              }"
              href="#"
              class="grey"
              title="Flip"
              @click.prevent="activeEl.flipHorizontal()"
            >
              <font-awesome-icon :icon="faExchange" />
            </a>
            <a
              v-if="showLayerTools"
              v-tml-tooltip.hover.right.nomobile="{
                message: 'Move up a layer',
              }"
              href="#"
              title="Move up a layer"
              class="grey"
              @click.prevent="moveItemUpLayer"
            >
              <font-awesome-icon class="rot-270" :icon="faCaretRight" />
            </a>
            <a
              v-if="showLayerTools"
              v-tml-tooltip.hover.right.nomobile="{
                message: 'Move down a layer',
              }"
              href="#"
              title="Move down a layer"
              class="grey"
              @click.prevent="moveItemDownLayer"
            >
              <font-awesome-icon class="rot-90" :icon="faCaretRight" />
            </a>
            <tml-dropdown-container
              v-if="showLayerTools"
              class="icon-container"
              align="right"
              vertical-align="middle"
              close-on-click-outside
              max-height="270px"
              @open="onDropdownOpen"
              @close="onDropdownClose"
            >
              <template #trigger="{trigger}">
                <a
                  v-tml-tooltip.hover.right.nomobile.dieonclick="{
                    message: 'Blend mode',
                  }"
                  href="#"
                  title="Blend mode"
                  class="grey"
                  @click.prevent="trigger"
                >
                  <font-awesome-icon :icon="faDiagramVenn" />
                </a>
              </template>
              <template #default="{trigger}">
                <ul class="modal-option">
                  <h5 class="mb-0 pb-1">Blend mode</h5>

                  <tml-close-button @click="trigger" />

                  <tml-dropdown-container-item
                    v-for="option in layerBlendingOptions"
                    :key="option.value"
                    class="pr-4"
                  >
                    <tml-anchor
                      class="flex px-4 py-2 items-center standard-text-color"
                      @click="setCompositeOperation(option.value)"
                    >
                      <font-awesome-icon
                        class="pr-4"
                        :class="{
                          'opacity-0':
                            option.value !== activeCompositeOperation,
                        }"
                        :icon="faCheck"
                      />
                      {{ option.text }}
                    </tml-anchor>
                  </tml-dropdown-container-item>
                </ul>
              </template>
            </tml-dropdown-container>
            <a
              v-if="showStripWhite()"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Remove white background',
              }"
              href="#"
              title="Remove white background"
              class="grey"
              @click.prevent="stripWhite"
            >
              <font-awesome-icon :icon="faWandMagicSparkles" />
            </a>
            <a
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Duplicate',
              }"
              href="#"
              title="Duplicate"
              class="grey"
              @click.prevent="copyActiveElement"
            >
              <font-awesome-icon :icon="faClone" />
            </a>
            <a
              v-if="activeEl.className === 'Text'"
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Edit',
              }"
              href="#"
              class="grey"
              title="Edit"
              @click.prevent="openTextEditor"
            >
              <font-awesome-icon :icon="faPencil" />
            </a>
            <a
              v-tml-tooltip.hover.right.nomobile.dieonclick="{
                message: 'Delete',
              }"
              href="#"
              title="Delete item"
              class="grey"
              @click.prevent="deleteActiveElement"
            >
              <font-awesome-icon :icon="faTrashAlt" />
            </a>
          </template>

          <a
            v-if="undoEnabled"
            v-tml-tooltip.hover.right.nomobile.dieonclick="{
              message: 'Undo',
            }"
            href="#"
            title="Undo"
            class="grey"
            @click.prevent="undo"
          >
            <font-awesome-icon :icon="faUndo" />
          </a>
        </template>

        <template v-else>
          <a
            href="#"
            class="grey col-picker mb-2"
            title="Recolour"
            @click.prevent="toggleColPalette()"
          >
            <font-awesome-icon :icon="faArrowCircleLeft" />
          </a>
          <div>
            <tml-pseudo-keyboard v-if="!isMobile()" :show="showColPalette">
              <template #content>
                <tml-tabbed-color-palette
                  :default-color="activeEl?.color || background?.fill"
                  :colours="[
                    'Black',
                    'Red',
                    'Yellow',
                    'Green',
                    'Blue',
                    'Purple',
                  ]"
                  circle-size="24px"
                  @colour-selected="$emit('recolour', $event)"
                />
              </template>
            </tml-pseudo-keyboard>
          </div>
        </template>
      </template>
      <template v-if="helpPopupName && !isInIframe">
        <tml-anchor
          ref="helpPopup"
          target="_blank"
          class="grey"
          @click="$overlays.open(helpPopupName)"
        >
          <font-awesome-icon :icon="faCircleInfo" />
        </tml-anchor>

        <tml-tooltip
          v-if="$refs.helpPopup !== undefined && showHelpTooltip"
          name="helpPopupTooltip"
          :target="$refs.helpPopup.$el"
          :ttl="0"
          title="Take a tour"
          :start-open="!Manufacturer.hasBadge('designer')"
          position="right"
          @dismissed="$emit('help-dismissed')"
        >
          <span class="tooltip">Get familiar with the features in Studio</span>
        </tml-tooltip>
      </template>
    </div>

    <tml-new-popup
      v-if="config.enableShareTool"
      name="studioShareDesignLinkBuilder"
      layout="standard"
      height="auto"
      title="Share your design"
    >
      <template #content>
        <p class="mb-4">
          Share this link with a friend and they'll be able to edit your design
        </p>
        <template v-if="shareDesignUrl">
          <tml-code-block :code="shareDesignUrl" :code-highlight="false" />
        </template>
        <tml-spinner v-else />
      </template>
      <template #footer>
        <p v-if="!isProStore()">
          You can also sell your designs with
          <a href="https://teemill.com" target="_blank" rel="noopener">
            Teemill
          </a>
        </p>
      </template>
    </tml-new-popup>
  </div>
</template>

<script>
import {Manufacturer} from '@teemill/common/models';

import {
  TmlNewSelect,
  TmlPseudoKeyboard,
  TmlTabbedColorPalette,
} from '@teemill/components';
import {formatUrl} from '@teemill/common/helpers';

import {
  faSearchPlus,
  faSearchMinus,
  faTrashAlt,
  faCaretRight,
  faWandMagicSparkles,
  faArrowCircleLeft,
  faShareAlt,
  faClone,
  faCircleInfo,
  faSlidersH,
  faCrop,
  faTh,
  faExpandArrowsAlt,
  faPalette,
  faUndo,
  faExchange,
  faPencil,
  faShirt,
  faDiagramVenn,
  faCheck,
  faInfoCircle,
  faCube,
} from '@fortawesome/pro-light-svg-icons';

import {overlays} from '@teemill/modules/overlays';
import {openUpgradePopup, backprintsBanner} from '@teemill/modules/pro';
import {capitalize} from 'lodash';

export default {
  name: 'StudioToolbar',

  components: {
    TmlPseudoKeyboard,
    TmlTabbedColorPalette,
  },

  inject: [
    'getCanvas',
    'isMobile',
    'getVuexProperty',
    'setVuexProperty',
    'config',
    'openTooltipOnce',
    'openTooltipOnceIfDoesNotHaveBadge',
    'app',
  ],

  props: {
    showTools: {
      type: Boolean,
    },

    zoom: {
      type: Number,
      default: 1,
    },

    activeEl: {
      type: [Object, Boolean],
    },

    objectCount: {
      type: Number,
      default: 0,
    },

    background: {
      type: [Object, Boolean],
    },

    showColPalette: {
      type: Boolean,
    },

    helpPopupName: {
      type: String,
    },

    showHelpTooltip: {
      type: Boolean,
    },
  },

  data() {
    return {
      shareDesignUrl: false,

      faSearchPlus,
      faSearchMinus,
      faTrashAlt,
      faCaretRight,
      faPalette,
      faWandMagicSparkles,
      faArrowCircleLeft,
      faShareAlt,
      faClone,
      faCircleInfo,
      faSlidersH,
      faCrop,
      faTh,
      faExpandArrowsAlt,
      faUndo,
      faExchange,
      faPencil,
      faShirt,
      faDiagramVenn,
      faCheck,
      faInfoCircle,
      faCube,

      Manufacturer,

      overlays,

      layerBlendingOptions: [
        {
          text: 'Standard',
          value: 'source-over',
        },
        {
          text: 'Clipping mask',
          value: 'destination-in',
        },
        {
          text: 'Multiply',
          value: 'multiply',
        },
        {
          text: 'Screen',
          value: 'screen',
        },
        {
          text: 'Overlay',
          value: 'overlay',
        },
        {
          text: 'XOR',
          value: 'xor',
        },
        {
          text: 'Hue',
          value: 'hue',
        },
        {
          text: 'Saturation',
          value: 'saturation',
        },
        {
          text: 'Color',
          value: 'color',
        },
        {
          text: 'Luminosity',
          value: 'luminosity',
        },
        {
          text: 'Hard light',
          value: 'hard-light',
        },
        {
          text: 'Soft light',
          value: 'soft-light',
        },
        {
          text: 'Lighten',
          value: 'lighten',
        },
        {
          text: 'Darken',
          value: 'darken',
        },
        {
          text: 'Underneath',
          value: 'destination-over',
        },
        {
          text: 'Inside content',
          value: 'source-atop',
        },
      ],

      dropDownOpen: false,

      activeLayer: {
        text: 'Front',
        value: 'front',
      },
    };
  },

  computed: {
    activeProductObject() {
      return this.getVuexProperty('activeProductObject');
    },

    showBackprintButton() {
      if (this.layerOptions.length > 2) {
        return false;
      }

      if (this.productSupportsBackprints && this.config.forceBackprintsOn) {
        return this.enableBackPrints();
      }

      return this.productSupportsBackprints;
    },

    productSupportsBackprints() {
      const activeProduct = this.activeProductObject;

      if (activeProduct) {
        const colour = activeProduct.colour;

        if (colour && activeProduct.colours[colour]) {
          // checks if the active product has a back image for the current colour
          return !!activeProduct.colours[colour].back_image_url;
        }
      }

      return false;
    },

    isShopify() {
      return (
        !!Manufacturer.active?.isNotTeemill() ||
        !!Manufacturer.active?.isTeemillIntegration()
      );
    },

    undoEnabled() {
      if (this.getCanvas()?.params?.history?.enabled) {
        return true;
      }

      return false;
    },

    // only show the layer tools if there is more than 1 layer
    showLayerTools() {
      return this.objectCount !== 1;
    },

    activeCompositeOperation() {
      return this.activeEl?.composite;
    },

    layerOptions() {
      const activeProduct = this.activeProductObject;

      if (activeProduct) {
        const colour = activeProduct.colour;

        if (colour && activeProduct.colours[colour]) {
          return Object.keys(activeProduct.colours[colour].layers).map(
            layer => ({
              text: capitalize(layer),
              value: layer,
            })
          );
        }
      }

      return [];
    },

    showLayerSelector() {
      return this.layerOptions.length >= 2;
    },

    layerSelectorDisabled() {
      return !this.enableBackPrints();
    },

    isInIframe() {
      return window.self !== window.top;
    },
  },

  watch: {
    activeLayer: {
      handler(val, oldVal) {
        if (!this.enableBackPrints()) {
          if (oldVal && val.value !== oldVal.value) {
            this.$nextTick(() => {
              this.activeLayer = oldVal;
            });
          }
          return;
        }

        this.getCanvas().setActiveLayer(val.value);
      },
    },

    layerOptions: {
      handler(val) {
        if (val.length) {
          this.activeLayer = val[0];
        }
      },
    },
  },

  methods: {
    /**
     * Finds the currently selected element in the canvas, if any are selected
     *
     * @return {OmniObject}
     */
    getActiveElement() {
      return this.getCanvas().activeEl;
    },

    /**
     * Duplicates the currently selected element
     */
    copyActiveElement() {
      const activeElement = this.getActiveElement();
      if (activeElement) {
        activeElement.duplicate();
      }
    },

    /**
     * Removes the currently selected element from the canvas
     *
     * @return {void}
     */
    deleteActiveElement() {
      this.getCanvas().deleteObject();
    },

    /**
     * Move a staged element up a layer
     *
     * @return {void}
     */
    moveItemUpLayer() {
      const activeElement = this.getActiveElement();
      if (activeElement) {
        activeElement.moveLayerUp();
      }
    },

    /**
     * Move a staged element down a layer
     *
     * @return {void}
     */
    moveItemDownLayer() {
      const activeElement = this.getActiveElement();
      if (activeElement) {
        activeElement.moveLayerDown();
      }
    },

    /**
     * Checks if the current image has any white pixels that can be removed
     *
     * @return {Boolean}
     */
    showStripWhite() {
      return this.activeEl.className === 'Image' && this.activeEl.containsWhite;
    },

    /**
     * Apply filters to remove the white areas from an image
     *
     * @return {void}
     */
    stripWhite() {
      const activeElement = this.getActiveElement();

      if (activeElement && typeof activeElement.addFilter !== 'undefined') {
        if (activeElement.filters && activeElement.filters.stripWhite) {
          activeElement.removeFilter('stripWhite');
        } else {
          activeElement.addFilter('stripWhite', 0.9);
        }
        activeElement.applyFilters();
        this.getCanvas().render();
      }
    },

    /**
     * Hide or show the colour paletter of predefined colours
     *
     * @return {void}
     */
    toggleColPalette() {
      this.$emit('toggle-col-palette');
    },

    shareDesign() {
      overlays.open('studioShareDesignLinkBuilder');

      const object = this.getShareableObject();

      this.axios
        .post(
          formatUrl('/omnis/v3/dashboard/studio/createShareableLink/'),
          object
        )
        .then(({data}) => {
          let path = this.$route.path;

          Object.keys(this.$route.params).forEach(key => {
            path = path.replace(`/${this.$route.params[key]}/`, '/');
          });
          if (data) {
            this.shareDesignUrl = `${window.location.origin}${path}${data}/`;
          }
        })
        .catch(() => {
          this.$dialogs.showSnackBar({
            message:
              'Sorry, something went wrong while trying to create a shareable link. Please try again later',
          });
        });
    },

    getShareableObject() {
      const cnv = this.getCanvas();
      const activeLayer = cnv.activeLayerName;
      let backDesign = null;
      let frontDesign = null;

      if (cnv.getObjects('front').length) {
        cnv.setActiveLayer('front');
        frontDesign = cnv.export('image/webp', 0.92, 4);

        cnv.setActiveLayer(activeLayer);
      }

      if (cnv.getObjects('back').length) {
        cnv.setActiveLayer('back');
        backDesign = cnv.export('image/webp', 0.92, 4);

        cnv.setActiveLayer(activeLayer);
      }

      return {
        base_sku: this.getVuexProperty('sku'),
        colour: this.getVuexProperty('colour'),
        front_design_base64: frontDesign,
        back_design_base64: backDesign,
      };
    },

    /**
     * Finds the appropriate zoom icon (zoom in or zoom out) depending on the
     * current zoom level
     *
     * @return {Object} a FontAwesome icon
     */
    getZoomIcon() {
      if (this.zoom > 1) {
        return this.faSearchMinus;
      }
      return this.faSearchPlus;
    },

    /**
     * Toggles between front and back Studio layers
     *
     * @return {void}
     */
    switchGarmentLayer() {
      if (this.enableBackPrints()) {
        const cnv = this.getCanvas();
        const activeLayer = cnv.params.stage.activeLayer;
        cnv.setActiveLayer(activeLayer === 'front' ? 'back' : 'front');
      } else {
        this.promptBackprintUpgrade();
      }
    },

    onLayerSelectorInteraction(event) {
      if (!this.layerSelectorDisabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.promptBackprintUpgrade();
    },

    promptBackprintUpgrade() {
      if (
        !this.isProStore() &&
        this.$project.active?.hasIntegration('website')
      ) {
        openUpgradePopup({
          origin: 'enable-backprints',
          title: 'Unlock backprints',
          description:
            'Take your products to the next level by unlocking the ability to add backprints.',
          image: backprintsBanner,
        });
      }
    },

    enableBackPrints() {
      if (this.config.enableBackPrints) {
        return true;
      }

      if (window.$store.state.subdomain?.company?.id === 52) {
        return true;
      }

      if (this.app.isSubdomain) {
        return true;
      }

      if (this.isShopify) {
        return true;
      }

      return false;
    },

    /**
     * Check if the current store has Teemill Pro
     *
     * @return {Boolean}
     */
    isProStore() {
      if (this.config.context === 'frontend') {
        return this.$store.state.subdomain.pro;
      }
      return false;
    },

    fill() {
      let method = 'cover';
      if (this.activeEl.height === this.getCanvas().boundingBox.height) {
        method = 'contain';
      }

      this.getCanvas().fillAndCenter(this.activeEl, method);
    },

    getRecolourBackgroundColour() {
      if (this.activeEl) {
        if (this.activeEl.color) {
          return this.activeEl.color;
        }
      }

      if (this.background?.fill) {
        return this.background.fill;
      }

      return 'black';
    },

    undo() {
      this.getCanvas().undo();
    },

    openTextEditor() {
      this.setVuexProperty('activeTab', 'text-editor');
      this.setVuexProperty('activeDesignTab', 'text');
    },

    setCompositeOperation(composite) {
      const cnv = this.getCanvas();

      if (!cnv) {
        return;
      }

      const activeEl = cnv?.activeEl;

      if (activeEl) {
        activeEl.composite = composite;
        cnv.render();
      }
    },

    onDropdownOpen() {
      this.dropDownOpen = true;
    },

    onDropdownClose() {
      this.dropDownOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.studio-controls {
  position: absolute;
  z-index: 9;
  font-size: 1.4em;
  top: 0.5em;
  border-radius: 0 0 0 2px;
  padding: 6px 0;
  line-height: 1;

  &.left {
    left: 0.5em;
    border-radius: 0 0 2px 0;
  }
  &.right {
    right: 0;
  }
  &.secondary {
    top: 60px;
    z-index: 8;
  }
  > a,
  > div.icon-container {
    background: rgba(255, 255, 255, 0.8);
    display: block;
    padding: 5px 0;
    clear: both;
    float: left;
    min-width: 50px;
    text-align: center;
  }
  &.bottom {
    bottom: 5px;
    top: auto;
  }

  .color-preview-container {
    padding: 0 14px;

    .color-preview {
      padding-bottom: 100%;
      border-radius: $border-radius;
      box-shadow: 0 0 2px $light-text-color;
    }
  }
}

@media (max-width: 991px) {
  .studio-controls {
    > a {
      padding: 10px 0;
      font-size: 120%;
    }

    max-height: calc(100vh - #{$header-height} - 60px);
    padding-bottom: 3em;

    &.with-sub-menu {
      overflow-y: unset;
    }

    &.secondary {
      overflow-y: auto;
    }
  }

  .layer-toggle {
    svg {
      height: 1.1em;
    }
  }
}

.layer-selector-wrapper--locked {
  cursor: pointer;
}

.rot-90 {
  transform: rotate(90deg);
}
.rot-180 {
  transform: rotate(180deg);
}
.rot-270 {
  transform: rotate(270deg);
}

.perspective-shift {
  transform: perspective(1cm) rotateY(25deg);
}

.tooltip {
  font-size: 1rem;
}

.modal-option {
  text-align: left;
  font-size: 1rem;

  h5 {
    padding: 0.75em 1em 0;
  }
}
</style>

<style lang="scss">
.layer-toggle {
  font-size: 30px;

  svg {
    height: 1em;
    width: auto;

    path {
      stroke: #555 !important;
    }
  }
}
</style>
